import dayjs from 'dayjs';
import {
  Dispatch,
  FC,
  FormEventHandler,
  RefObject,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useHistory, useTemplates } from 'contexts';
import { useSelectedLocation } from 'contexts/selected-location';
import { Booking, BookingDetails, Template } from 'types';

import { useDialog } from 'components';
import { useBookingApi } from 'hooks/use-booking-api';
import NewBookingForm from './new-booking-form';

type NewBookingContextType = {
  booking: Partial<Booking>;
  dialogProps: Omit<ReturnType<typeof useDialog>, 'openDialog'>;
  formRef: RefObject<HTMLFormElement>;
  setBooking: Dispatch<SetStateAction<Partial<Booking>>>;
  setDetails: (details?: BookingDetails, index?: number) => void;
  handleReset: FormEventHandler<HTMLFormElement>;
  handleSubmit: FormEventHandler<HTMLFormElement>;
  handleSaveAs: () => void;
};

const NewBookingContext = createContext<NewBookingContextType>({
  booking: {},
  dialogProps: { closeDialog: () => {}, ref: { current: null } },
  formRef: { current: null },
  setBooking: () => {},
  setDetails: () => {},
  handleReset: () => {},
  handleSubmit: () => {},
  handleSaveAs: () => {}
});

export const useNewBooking = () => useContext(NewBookingContext);

const NewBooking: FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { templateName } = useParams();
  const { lastLocation } = useHistory();

  const { postBooking } = useBookingApi();
  const { openDialog, ...dialogProps } = useDialog();
  const { selectedLocation } = useSelectedLocation();
  const { templates, addTemplate } = useTemplates();

  const formRef = useRef<HTMLFormElement>(null);

  const [booking, setBooking] = useState<Partial<Booking>>({
    bookingDetails: [{ carb: false, count: 1, iso: [] } as BookingDetails],
    customer: undefined,
    createdBy: localStorage.getItem('username') ?? undefined,
    location: selectedLocation ?? undefined,
    ...(templateName && templates.find(({ name }) => name === templateName)),
    startDate: dayjs().valueOf(),
    expirationDate: dayjs().add(1, 'month').valueOf()
  });

  const setDetails = (details?: BookingDetails, index?: number) => {
    setBooking(previous => {
      const bookingDetails = Object.assign([], previous.bookingDetails);
      if (!details) bookingDetails.splice(index ?? bookingDetails.length, 1);
      else bookingDetails.splice(index ?? bookingDetails.length, 1, details);
      if (bookingDetails.length < 1) bookingDetails.push({ iso: [], count: 0 } as BookingDetails);
      return { ...previous, bookingDetails };
    });
  };

  const handleReset: FormEventHandler<HTMLFormElement> = useCallback(() => {
    navigate(lastLocation ?? '/bookings', { replace: true });
  }, [lastLocation, navigate]);

  const handleSaveAs = useCallback(() => {
    if (templateName) {
      addTemplate({ ...booking, name: templateName } as Template);
    } else {
      openDialog();
    }
  }, [booking, templateName, addTemplate, openDialog]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      if (pathname.includes('templates')) {
        /* save template */
        if (templateName) handleSaveAs();
        else addTemplate({ ...booking, name: templateName } as Template);
      } else {
        /* save booking */
        const response = await postBooking({ ...booking, name: undefined, id: undefined as any } as Booking);
        if (response) navigate(`/bookings/${response.id}`);
      }
    },
    [booking, pathname, templateName, addTemplate, navigate, handleSaveAs, postBooking]
  );

  return (
    <NewBookingContext.Provider
      value={{
        booking,
        dialogProps,
        formRef,
        setBooking,
        setDetails,
        handleReset,
        handleSaveAs,
        handleSubmit
      }}
    >
      <NewBookingForm />
    </NewBookingContext.Provider>
  );
};

export default NewBooking;
